import { createElement as _createElement } from "react";
import { jsx as _jsx, jsxs as _jsxs, Fragment as _Fragment } from "react/jsx-runtime";
import { useState, Suspense } from "react";
import purifyMarked from "@ploy-lib/purify-marked";
import { defineMessages, FormattedMessage } from "react-intl";
import ContentBox from "../../ContentBox";
import ContentListItem from "../../ContentListItem";
import DetailsBox from "../../DetailsBox";
import Typography from "@material-ui/core/Typography";
import { makeStyles } from "@material-ui/core/styles";
import WarningIcon from "@material-ui/icons/Warning";
import ScheduleIcon from "@material-ui/icons/Schedule";
import MailIcon from "@material-ui/icons/Mail";
import ChatIcon from "@material-ui/icons/Chat";
import { DividerItems } from "@ploy-ui/core";
import { DocumentStatus, ApplicationStatus } from "@ploy-lib/types";
import { DocumentType } from "@ploy-lib/rest-resources";
import { TaskListItem, useTasks } from "../Todos";
import { Divider, List, Button } from "@material-ui/core";
import Skeleton from "@material-ui/lab/Skeleton";
import { useHeight } from "../../hooks/useHeight";
import KeyboardArrowUpIcon from "@material-ui/icons/KeyboardArrowUp";
import KeyboardArrowDownIcon from "@material-ui/icons/KeyboardArrowDown";
import { useNewsListResources } from "./useNewsListResources";
const messages = defineMessages({
    awaitingRegAppPrimaryMessage: {
        id: "dploy.dashboard.newscontent.AwaitingOwnerRegistration.primary",
        defaultMessage: "Avventer eierregistrering"
    },
    awaitingRegAppSecondaryMessage: {
        id: "dploy.dashboard.newscontent.AwaitingOwnerRegistration.secondary",
        defaultMessage: "Det er {amount, number} kjøretøy som ikke har blitt omregistrert."
    },
    fullySignedIncompletePrimaryMessage: {
        id: "dploy.dashboard.newscontent.FullySignedIncomplete.primary",
        defaultMessage: "Ferdig signert med mangler"
    },
    fullySignedIncompleteSecondaryMessage: {
        id: "dploy.dashboard.newscontent.FullySignedIncomplete.secondary",
        defaultMessage: "{amount, plural,\none {# avtale}\nother {# avtaler}\n  } er ferdig signert, men mangler informasjon."
    },
    missingRegNumberPrimaryMessage: {
        id: "dploy.dashboard.newscontent.MissingRegistrationNumber.primary",
        defaultMessage: "{amount, number} kjøretøy avventer registreringsnummer"
    },
    missingRegNumberSecondaryMessage: {
        id: "dploy.dashboard.newscontent.MissingRegistrationNumber.secondary",
        defaultMessage: "Pantedokument er ikke elektronisk signert av kunde."
    }
});
const newsTasks = [
    {
        id: 1,
        name: "missing-reg-number-apps",
        filter: {
            allTasksSignedExceptType: [DocumentType.Security],
            docStatus: [DocumentStatus.PartiallySigned],
            appStatus: [
                ApplicationStatus.ManuallyApproved,
                ApplicationStatus.ApprovedWithConditions
            ],
            salepersonId: "current"
        },
        primaryMessage: messages.missingRegNumberPrimaryMessage.id,
        secondaryMessage: messages.missingRegNumberSecondaryMessage.id,
        icon: _jsx(ScheduleIcon, {})
    },
    {
        id: 2,
        name: "awaiting-reg-app",
        filter: {
            docStatus: [DocumentStatus.AwaitingOwnerRegistration],
            appStatus: [
                ApplicationStatus.ManuallyApproved,
                ApplicationStatus.ApprovedWithConditions
            ],
            salepersonId: "current"
        },
        primaryMessage: messages.awaitingRegAppPrimaryMessage.id,
        secondaryMessage: messages.awaitingRegAppSecondaryMessage.id,
        icon: _jsx(ScheduleIcon, {})
    },
    {
        id: 3,
        name: "incomplete-apps",
        filter: {
            docStatus: [DocumentStatus.FullySignedIncomplete],
            appStatus: [
                ApplicationStatus.ManuallyApproved,
                ApplicationStatus.ApprovedWithConditions
            ],
            salepersonId: "current"
        },
        primaryMessage: messages.fullySignedIncompletePrimaryMessage.id,
        secondaryMessage: messages.missingRegNumberSecondaryMessage.id,
        icon: _jsx(ScheduleIcon, {})
    }
];
function News(props) {
    const { id, showInfoMessage, showTasks, boxProps, applicationsSearchPath, setApplicationsSearchParams, emptyMessage, onChangeHeight, onResetHeight, placeholder } = props;
    const classes = useStyles();
    const [detailsId, handleClick] = useState();
    const handleBack = () => handleClick(undefined);
    const header = (_jsx(FormattedMessage, { id: "dealer.dashboard.newslist.header", defaultMessage: "Messages from {name}", description: "Header for newslist on dashboard. Params: name", values: {
            name: (_jsx(FormattedMessage, { id: "tenant.name", defaultMessage: "creditor", description: "The name of the current tenant (financial institution)" }))
        } }));
    const [{ info, newsList }, { error }] = useNewsListResources({
        showInfoMessage,
        placeholder
    });
    const [listRef] = useHeight(500);
    const overflowHeight = (listRef.current?.scrollHeight ?? 0) - (listRef.current?.offsetHeight ?? 0);
    const detailsItem = detailsId && newsList?.find(x => x.newsItemId === detailsId);
    const tasks = useTasks(showTasks ? newsTasks : [], placeholder);
    let detailsContent = undefined;
    if (detailsId === "info" && info != null) {
        detailsContent = (_jsxs("article", { className: classes.article, children: [_jsx(Typography, { variant: "subtitle1", className: classes.detailsHeader, children: info.header }), _jsx(Typography, { children: info.message })] }));
    }
    else if (detailsItem) {
        detailsContent = (_jsxs("article", { className: classes.article, children: [_jsx(Typography, { variant: "subtitle1", className: classes.detailsHeader, children: detailsItem.newsHeader }), _jsx(Typography, { gutterBottom: true, children: detailsItem.newsIngress }), detailsItem.newsContent && (_jsx(Typography, { variant: "body2", component: "div", dangerouslySetInnerHTML: {
                        __html: purifyMarked(detailsItem.newsContent)
                    } }))] }));
    }
    if (detailsContent) {
        return (_jsx(DetailsBox, { avatar: _jsx(ChatIcon, {}), ...boxProps, header: boxProps?.header || header, error: error, onClick: handleBack, children: detailsContent }));
    }
    const animation = !placeholder && undefined;
    return (_jsx(ContentBox, { avatar: _jsx(ChatIcon, {}), ...boxProps, header: boxProps?.header || header, scrollGradient: true, error: error, actions: _jsx(_Fragment, { children: onResetHeight ? (_jsx(Button, { color: "primary", onClick: id ? () => onResetHeight(id) : undefined, endIcon: _jsx(KeyboardArrowUpIcon, {}), children: _jsx(FormattedMessage, { id: "dploy.dashboard.newslist.show-fewer", description: "Show fewer messages", defaultMessage: "Se f\u00E6rre meldinger" }) })) : overflowHeight > 0 ? (_jsx(Button, { color: "primary", onClick: id
                    ? () => onChangeHeight?.(id, {
                        type: "relative",
                        value: overflowHeight
                    })
                    : undefined, endIcon: _jsx(KeyboardArrowDownIcon, {}), disabled: !onChangeHeight, children: _jsx(FormattedMessage, { id: "dploy.dashboard.newslist.show-more", description: "Show more messages", defaultMessage: "Se flere meldinger" }) })) : null }), children: _jsxs(List, { ref: listRef, dense: true, className: classes.list, children: [(info?.message || info?.header) && (_jsxs(_Fragment, { children: [_jsx(ContentListItem, { icon: _jsx(WarningIcon, {}), primary: info?.header, secondary: info?.message, button: true, onClick: () => handleClick("info") }, "info"), _jsx(Divider, {})] })), _jsx(Suspense, { fallback: _jsxs(_Fragment, { children: [_jsx(ContentListItem, { iconClass: tasks[0]?.iconClass, icon: tasks[0]?.icon, primary: _jsx(Skeleton, { animation: animation, variant: "text", width: "60%" }), secondary: _jsx(Skeleton, { animation: animation, variant: "text", width: "90%" }), hideChevron: true }), _jsx(Divider, {})] }), children: tasks.map(task => (_createElement(TaskListItem, { ...task, key: task.name, applicationsSearchPath: applicationsSearchPath, setApplicationsSearchParams: setApplicationsSearchParams }))) }), newsList && newsList.length === 0 && (_jsx(ContentListItem, { secondary: emptyMessage ?? (_jsx(FormattedMessage, { id: "dploy.dashboard.newslist.empty", defaultMessage: "Ingen meldinger" })), hideChevron: true })), _jsx(DividerItems, { children: newsList?.map(({ newsHeader, newsIngress, newsItemId }) => (_jsx(ContentListItem, { icon: _jsx(MailIcon, {}), primary: newsHeader?.substring(0, 140), secondary: newsIngress?.substring(0, 140), button: true, onClick: () => handleClick(newsItemId) }, newsItemId))) ?? (_jsx(ContentListItem, { icon: _jsx(MailIcon, {}), primary: _jsx(Skeleton, { animation: animation, variant: "text", width: "60%" }), secondary: _jsx(Skeleton, { animation: animation, variant: "text", width: "90%" }), hideChevron: true })) })] }) }));
}
News.displayName = "DployDashboardNews";
const useStyles = makeStyles({
    detailsHeader: {
        fontWeight: "bold"
    },
    list: {
        height: "100%"
    },
    article: {
        overflow: "auto"
    }
}, { name: "DashboardNewsList" });
export default News;
//# sourceMappingURL=News.js.map